import { useState, useEffect } from 'react';
import { ReactComponent as ProfilePicture } from '@Assets/blankProfile.svg';
import { IoClose as Close } from 'react-icons/io5';
import { MdPerson as PersonIcon, MdLocationOn as LocationIcon, MdBusiness as OrgIcon, MdWork as ProjectIcon, MdEmail as EmailIcon } from 'react-icons/md';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import ErrorHandler from '@Root/ErrorHandler';
import './SearchUserProfileView.scss';

export default function SearchUserProfileView(props) {

    const [user, setUser] = useState({});
    const [orgs, setOrgs] = useState([]);
    const [comOrgs, setComOrgs] = useState([]);
    const [comProjects, setComProjects] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const userInfoService = new UserInfoService();
    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();
    const activeUserId = localStorage.getItem('activeUserId');
    const className = 'searchUserProfileView';

    useEffect(() => {
        if(!!props.profileId) renderData();
    }, [props]);

    const renderData = async () => {
        setLoading(true);
        try {
            let userObj = await userInfoService.getUserById(props.profileId);
            userObj = JSON.parse(userObj.body);
            setUser(userObj);
            let accessList = await userAuthService.getAccessListForUser(props.profileId);
            accessList = JSON.parse(accessList.body);
            let activeUserAccessList = !!localStorage.getItem('activeUserAccessList') ? JSON.parse(localStorage.getItem('activeUserAccessList')) : {};
            if(Object.keys(activeUserAccessList).length == 0) {
                activeUserAccessList = await userAuthService.getAccessListForUser(activeUserId);
                activeUserAccessList = JSON.parse(activeUserAccessList.body);
            }
            setOrgs(accessList.orgs);
            setProjects(accessList.projects);
            setComOrgs(accessList?.orgs?.filter(o => activeUserAccessList?.orgs?.some((org) => o.id === org.id)));
            setComProjects(accessList?.projects?.filter(p => activeUserAccessList?.projects?.some((project) => p.id === project.id)));
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], {type: mime});
    }

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <PersonIcon className={`${className}__header__icon`} />
                            <h2 className={`${className}__header__title`}>User Profile</h2>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}>
                            <Close />
                        </button>
                    </div>

                    {loading ? (
                        <div className={`${className}__loading`}>
                            <CircularProgress size={40} />
                            <p>Loading profile...</p>
                        </div>
                    ) : (
                        <div className={`${className}__content`}>
                            {/* Left Panel - Profile Info */}
                            <div className={`${className}__sidebar`}>
                                <div className={`${className}__profile`}>
                                    <div className={`${className}__profile__avatar`}>
                                        {user?.photo !== undefined && user?.photo !== 'null' ? (
                                            <img src={URL.createObjectURL(dataURLtoFile(user?.photo))} alt='Profile' />
                                        ) : (
                                            <ProfilePicture />
                                        )}
                                    </div>
                                    <div className={`${className}__profile__name`}>
                                        {user?.fullName}
                                    </div>
                                    <div className={`${className}__profile__username`}>
                                        @{user?.userName}
                                    </div>
                                    {user?.designation && (
                                        <div className={`${className}__profile__designation`}>
                                            {user?.designation}
                                        </div>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className={`${className}__stats`}>
                                    {user?.country && (
                                        <div className={`${className}__stat`}>
                                            <LocationIcon />
                                            <span>
                                                {user?.country}
                                                {user?.state && `, ${user?.state}`}
                                            </span>
                                        </div>
                                    )}
                                    <div className={`${className}__stat`}>
                                        <OrgIcon />
                                        <span>{orgs?.length || 0} Organizations</span>
                                    </div>
                                    <div className={`${className}__stat`}>
                                        <ProjectIcon />
                                        <span>{projects?.length || 0} Projects</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel - Details */}
                            <div className={`${className}__main`}>
                                {/* Bio Section */}
                                {user?.about && (
                                    <div className={`${className}__section`}>
                                        <h3 className={`${className}__section__title`}>Bio</h3>
                                        <p className={`${className}__section__text`}>
                                            {user?.about}
                                        </p>
                                    </div>
                                )}

                                {/* Common Organizations */}
                                {comOrgs?.length > 0 && props.profileId != activeUserId && (
                                    <div className={`${className}__section`}>
                                        <h3 className={`${className}__section__title`}>
                                            Common Organizations
                                        </h3>
                                        <p className={`${className}__section__subtitle`}>
                                            You and @{user?.userName} are both part of these organizations:
                                        </p>
                                        <div className={`${className}__list`}>
                                            {comOrgs.map((org, index) => (
                                                <div key={index} className={`${className}__list__item`}>
                                                    <OrgIcon />
                                                    <span>{org.orgName}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Common Projects */}
                                {comProjects?.length > 0 && props.profileId != activeUserId && (
                                    <div className={`${className}__section`}>
                                        <h3 className={`${className}__section__title`}>
                                            Common Projects
                                        </h3>
                                        <p className={`${className}__section__subtitle`}>
                                            You and @{user?.userName} are both part of these projects:
                                        </p>
                                        <div className={`${className}__list`}>
                                            {comProjects.map((project, index) => (
                                                <div key={index} className={`${className}__list__item`}>
                                                    <ProjectIcon />
                                                    <span>{project.projectName}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Empty State */}
                                {!user?.about && comOrgs?.length === 0 && comProjects?.length === 0 && (
                                    <div className={`${className}__empty`}>
                                        <PersonIcon />
                                        <p>No additional information available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
